import {useState} from 'react';
import { Icon } from 'semantic-ui-react';
import getNotificationIcon from './getNotificationIcon';
import { format_date } from './format_date';
import SIcon from '../../views/NUI/StreamlineIcons/SIcon';
import { cls } from '../../views/NUI/utils';

const NotificationItem = ({
    notification,
    onClick,
    hasUnreadButton,
    setNotificationRead,
    getPlugin,
    onClose,
}) => {
    const plugin = getPlugin?.(notification.type);
    const [expanded, setExpand] = useState(false);

    const unread_dynamic_colors = {
        background: 'bg-blue-50',
        background_active: 'hover:bg-blue-100',
        plugins_background: 'bg-blue-50/50',
        envelope_icon: 'text-blue-500',
        pin_icon: 'text-blue-500',
    };

    const unread_regular_colors = {
        background: 'bg-white',
        background_active: 'hover:bg-gray-100',
        plugins_background: 'bg-gray-50',
        envelope_icon: 'text-gray-500',
        pin_icon: 'text-gray-500',
    };

    const read_colors = {
        background: 'bg-[#EFEFF0]',
        background_active: 'hover:bg-[#E5E5E6]',
        plugins_background: 'bg-gray-100',
        envelope_icon: 'text-gray-500',
        pin_icon: 'text-gray-500',
    };

    const background_class =
        isUnread(notification)
            ? (notification.isDynamic)
                ? unread_dynamic_colors.background
                : unread_regular_colors.background
            : read_colors.background;

    const background_active_class =
        isUnread(notification)
            ? (notification.isDynamic)
                ? unread_dynamic_colors.background_active
                : unread_regular_colors.background_active
            : read_colors.background_active;

    const plugins_background_class =
        isUnread(notification)
            ? (notification.isDynamic)
                ? unread_dynamic_colors.plugins_background
                : unread_regular_colors.plugins_background
            : read_colors.plugins_background;

    const envelope_icon_class =
        isUnread(notification)
            ? notification.isDynamic
                ? unread_dynamic_colors.envelope_icon
                : unread_regular_colors.envelope_icon
            : read_colors.envelope_icon;

    const pin_icon_class =
        isUnread(notification)
            ? notification.isDynamic
                ? unread_dynamic_colors.pin_icon
                : unread_regular_colors.pin_icon
            : read_colors.pin_icon;

    const n_link = notification?.link?.replace(/^\//, "");
    const subheading = notification?.extra_fields?.subheading || notification?.subheading;
    const usr = app.acl.is_usr;
    return (
        <div className="notification-item relative" data-testid="item-notification" data-unread={String(isUnread(notification))} data-link={notification.link || notification.banner_link}>
            <div className={`
                notification-item-main
                w-full flex flex-col 
                items-center justify-between
                relative
            `}>
                <NotificationItemButton
                    active={!usr && onClick && notification.link}
                    notification={notification}
                    onClick={onClick}
                    hasUnreadButton={hasUnreadButton}
                    background_class={background_class}
                    background_active_class={background_active_class}
                >
                    <div className="notification-item-content flex flex-col gap-1 relative pr-[80px]">
                        <div className="notification-header flex items-center gap-2">
                            <div className="notification-icon h-[32px]">
                                {usr ? <SIcon {...getNotificationIcon(notification, subheading)} size="s" cls="text-is2-gray-body" bold />
                                : <Icon {...getNotificationIcon(notification)} aria-hidden="true" size="large" className="text-is2-gray-body"/>}
                            </div>
                            <div className="notification-header-content flex flex-col">
                                <div className="notification-header-title font-bold text-sm text-is2-gray-body">
                                    {usr ? subheading
                                        :
                                        <>
                                            {notification.title}
                                            {notification.link && ' ›'}
                                        </>
                                    }
                                </div>
                                
                                {!notification.isDynamic && (
                                    <div className="notification-header-date text-sm text-gray-500">
                                        {format_date(notification.created)}
                                    </div>
                                )}
                            </div>
                        </div>
                        {notification.title && usr && (
                            <div className="notification-title text-base text-black/85 [&_p]:mb-0">
                                {notification.title}
                            </div>
                        )}
                    </div>
                    <div className="notification-body text-base text-sm text-black/85 [&_p]:m-0 [&_p]:text-sm [&_p]:font-normal [&_span]:font-normal">
                        {expanded && <div className="mt-4">{notification.body}</div>}
                    </div>

                    {usr && (notification.body || notification.link) && (
                        <button data-testid="button-expand" className={cls("absolute right-4 top-[50px] bg-is2-blue-50 text-is2-gray-body rounded-full w-[40px] h-[40px] flex items-center justify-center cursor-pointer shrink-0", expanded && "-rotate-180")} onClick={() => {
                            setExpand(!expanded)
                            setNotificationRead && !notification.read && setNotificationRead?.(notification.notification_id, !notification.read)
                            }} title="View more">
                            <SIcon name="chevron" size="xxs" bold />
                        </button>
                    )}
                </NotificationItemButton>

                {expanded && notification?.link && usr &&
                    <div className="py-5 px-4 bg-is2-gray-10 w-full">
                        {plugin?.itemExtra && (
                            <div className={`notification-item-plugins flex flex-wrap gap-2 items-center`}>
                                <plugin.itemExtra
                                    notification={notification}
                                    onClose={onClose ? onClose : () => undefined}
                                />
                            </div>
                        )
                        || notification.type === 'patient_script_rejection' &&
                        <button className="ui button basic fluid" onClick={() => window.Intercom("show")} title="Go to support">
                            Speak to our support team
                        </button>
                        || <button data-testid="button-view" className="ui button basic fluid" onClick={() => onClick(notification)} title="Go to page">
                            View {['certificates','medications','referrals'].includes(n_link) ? n_link : "details"}
                        </button>}
                    </div>
                }

                {hasUnreadButton && !notification.overrides?.persistent && (
                    <NotifcationEnvelopButton
                        read={notification.read}
                        onClick={() => setNotificationRead && setNotificationRead(notification.notification_id, !notification.read)}
                        background_class={background_class}
                        background_active_class={background_active_class}
                        envelope_icon_class={envelope_icon_class}
                    />
                )}
            </div>
            {showPinIcon(notification)
                ? <Icon
                    name="pin"
                    className={`absolute isolate z-10 top-2.5 right-1 ${pin_icon_class}`}
                    size="small"
                    aria-hidden="true"
                />
                : null
            }
           
        </div>
    );
};

function NotificationItemButton({
    active,
    notification,
    children,
    onClick,
    hasUnreadButton,
    background_class,
    background_active_class,
}) {
    if (active) {
        return (
            <button
                className={`
                    notification-item-card-button
                    flex-1 text-left
                    focus:outline-none focus:ring-2 focus:ring-offset-2
                    focus:ring-blue-500
                    w-full
                    
                    transition-colors duration-150
                    px-4 py-6
                    focus:relative
                    focus:isolation-isolate
                    hover:isolation-isolate
                    focus:z-10
                    ${background_class}
                    ${background_active_class}
                `}
                onClick={() => onClick(notification)}
                title="Go to page"
            >
                {children}
            </button>
        );
    }
    return (
        <div
            className={`
                notification-item-card
                flex-1 text-left
                transition-colors duration-150
                px-4 py-6
                w-full
                ${background_class}
            `}
        >
            {children}
        </div>
    );
}

function NotifcationEnvelopButton({
    read,
    onClick,
    background_class,
    background_active_class,
    envelope_icon_class,
}) {
    return (
        <button
            className={`
                    notification-item-read-button
                    focus:outline-none focus:ring-2 focus:ring-offset-2
                    focus:ring-blue-500
                    sm:[li:first-child_&]:rounded-tr-lg
                    [li:last-child_&]:rounded-br-lg
                    p-4
                    self-stretch
                    relative
                    focus:isolation-isolate
                    hover:isolation-isolate
                    focus:z-10
                    ${background_class}
                    ${background_active_class}
                `}
            onClick={onClick}
            aria-label={`Mark notification as ${read ? 'unread' : 'read'}`}
            title={`Mark notification as ${read ? 'unread' : 'read'}`}
        >
            <Icon
                name={read ? 'envelope open' : 'envelope'}
                className={`${envelope_icon_class} transform translate-x-0.5`}
                size="large"
                aria-hidden="true"
            />
        </button>
    );
}

function showPinIcon(notification) {
    if (notification.isDynamic) {
        return true;
    }
    return (
        isUnread(notification)
        && (
            notification.priority === 'urgent' ||
            notification.priority === 'high' ||
            notification.overrides?.pinned
        )
    );
}

function isUnread(notification) {
    return !notification.read;
}

export default NotificationItem; 
